import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Alert } from '../../types/index';
import { MockTradingAPI } from '../../services/mockAPI';
import { AlertCard } from '../molecules/AlertCard';
import { Modal } from '../atoms/Modal';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { Select } from '../atoms/Select';

interface NewAlertData {
  symbol: string;
  condition: 'above' | 'below';
  targetPrice: string | number;
}

export const AlertsPanel: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [newAlert, setNewAlert] = useState<NewAlertData>({ symbol: '', condition: 'above', targetPrice: '' });
  const queryClient = useQueryClient();

  const { data: alerts } = useQuery<Alert[]>({
    queryKey: ['alerts'],
    queryFn: MockTradingAPI.getAlerts,
  });

  const createMutation = useMutation<Alert, Error, Omit<Alert, 'id' | 'created'>>({
    mutationFn: MockTradingAPI.createAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setShowCreateForm(false);
      setNewAlert({ symbol: '', condition: 'above', targetPrice: '' });
    },
  });

  const deleteMutation = useMutation<boolean, Error, string>({
    mutationFn: MockTradingAPI.deleteAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const handleCreateAlert = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      ...newAlert,
      targetPrice: parseFloat(newAlert.targetPrice as string),
    });
  };

  return (
    <div data-testid="alerts-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Price Alerts</h2>
        <Button onClick={() => setShowCreateForm(true)} data-testid="create-alert-button">
          <Plus size={20} />
          Create Alert
        </Button>
      </div>

      <div className="space-y-4">
        {alerts?.map(alert => (
          <AlertCard
            key={alert.id}
            alert={alert}
            onEdit={() => { }}
            onDelete={deleteMutation.mutate}
          />
        ))}
      </div>

      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Price Alert"
      >
        <form onSubmit={handleCreateAlert} className="space-y-4">
          <Input
            label="Stock Symbol"
            value={newAlert.symbol}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewAlert({ ...newAlert, symbol: e.target.value.toUpperCase() })
            }
            placeholder="e.g., AAPL"
            required
            data-testid="alert-symbol-input"
          />

          <Select
            label="Condition"
            options={[
              { value: 'above', label: 'Price goes above' },
              { value: 'below', label: 'Price goes below' },
            ]}
            value={newAlert.condition}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setNewAlert({ ...newAlert, condition: e.target.value as 'above' | 'below' })
            }
            data-testid="alert-condition-select"
          />

          <Input
            label="Target Price"
            type="number"
            step="0.01"
            value={newAlert.targetPrice}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNewAlert({ ...newAlert, targetPrice: e.target.value })
            }
            required
            data-testid="alert-price-input"
          />

          <div className="flex gap-2">
            <Button type="submit" className="flex-1" data-testid="save-alert-button">
              Create Alert
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
